import { useState, useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import { ReactComponent as LocationIcon } from "../../../images/icons/location.svg";
import { ReactComponent as BussinesIcon } from "../../../images/icons/bussines.svg";
import { ReactComponent as EnvelopeIcon } from "../../../images/icons/envelope.svg";
import { ReactComponent as PersonIcon } from "../../../images/icons/NavDrawer/account.svg";
import { ReactComponent as EditIcon } from "../../../images/icons/edit-alternative.svg";
import { ReactComponent as CopyIcon } from "../../../images/icons/copy.svg";
import { ReactComponent as AcceptIcon } from "../../../images/icons/accept.svg"
import EditModal from "../../common/Modals/EditModal";
import ShowSeparateNotes from "./ShowSeparateNotes"
import { usStates } from "../../../lib/const"
import { splitRecipientName, combineRecipientName, capitalizeFirstLetter } from "../../../lib/utils"
import { useLabOrderDetailsStore } from '../../../stores'
import Tooltip from '../../common/Tooltip'

const Details = () => {
  const { 
    order, 
    isAdmin, 
    notes, 
    activeTab, 
    address, 
    setAddress, 
    updateOrder, 
    updateShippingAddress,
    isNotesModalOpen,
    setIsNotesModalOpen,
    isAddressModalOpen,
    setIsAddressModalOpen,
    resetNotesFromOrder,
    resetAddressFromOrder,
  } = useLabOrderDetailsStore();

  const attributes = order?.data?.attributes;
  
  const [loading, setLoading] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [editNotes, setEditNotes] = useState({});
  const [editAddress, setEditAddress] = useState({});

  useEffect(() => {
    if (attributes) {
      // Split recipient into firstName and lastName
      const recipient = attributes?.shippingAddress?.recipient ?? "";
      const { firstName, lastName } = splitRecipientName(recipient);

      setAddress({
        street: attributes?.shippingAddress?.street ?? "",
        street2: attributes?.shippingAddress?.street2 ?? "",
        city: attributes?.shippingAddress?.city ?? "",
        stateProvince: attributes?.shippingAddress?.stateProvince ?? "",
        zip: attributes?.shippingAddress?.zip ?? "",
        firstName: firstName,
        lastName: lastName,
        recipient: recipient
      });
    }
  }, [order?.data?.id, attributes]);

  // Initialize edit state when modal opens
  useEffect(() => {
    if (isNotesModalOpen) {
      setEditNotes({
        ...notes,
        [activeTab]: notes[activeTab] ?? ""
      });
    }
  }, [isNotesModalOpen, activeTab, notes]);

  useEffect(() => {
    if (isAddressModalOpen) {
      setEditAddress({ ...address });
    }
  }, [isAddressModalOpen, address]);

  const fieldMapping = {
    client: "notes",
    admin: "internal_notes",
    boutique: "boutique_shipping_note",
  };

  const openEditModal = (type) => {
    if (type === "notes") {
      setIsNotesModalOpen(true);
    } else if (type === "address") {
      setIsAddressModalOpen(true);
    }
  };

  const handleNoteChange = (value) => {
    setEditNotes({ 
      ...editNotes, 
      [activeTab]: value 
    });
  };

  const saveNotes = async () => {
    if (!updateOrder) return;
    
    setLoading(true);
    const dbField = fieldMapping[activeTab];
    if (!dbField) {
      setLoading(false);
      return;
    }

    const noteValue = editNotes[activeTab] ?? "";
    
    if (!attributes?.guid) {
      console.error("Order GUID is missing");
      setLoading(false);
      return;
    }

    try {
      await updateOrder(attributes.guid, dbField, noteValue);
      setLoading(false);
      setIsNotesModalOpen(false);
    } catch (error) {
      console.error("Error saving notes:", error);
      setLoading(false);
    }
  };

  const saveAddress = async () => {
    setLoading(true);
    try {
      // Combine firstName and lastName into recipient before saving
      const addressWithRecipient = {
        ...editAddress,
        recipient: combineRecipientName(editAddress.firstName, editAddress.lastName)
      };
      await updateShippingAddress(attributes?.shippingAddress?.guid, addressWithRecipient);
      setLoading(false);
      setIsAddressModalOpen(false);
    } catch (err) {
      console.error("Failed to update address:", err);
      setLoading(false);
      setIsAddressModalOpen(false);
    }
  };

  // Reset fields on cancel 
  const handleNotesModalClose = () => {
    resetNotesFromOrder();
    setIsNotesModalOpen(false);
  };

  const handleAddressModalClose = () => {
    resetAddressFromOrder();
    setIsAddressModalOpen(false);
  };

  const copyOrderDetails = () => {

    const toTitleCase = (str) => {
      if (!str) return "";
      return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const photographerName = address?.recipient || "";
    const street = toTitleCase(address.street || "");
    const city = toTitleCase(address.city || "");
    const state = address.stateProvince || "";
    const zip = address.zip || "";

    const infoText = [
      photographerName,
      street,
      `${city}, ${state} ${zip}`.trim()
    ].filter(line => line).join("\n");

    navigator.clipboard.writeText(infoText)
      .then(() => {
        setIsCopy(true)
        setTimeout(() => {
          setIsCopy(false)
        }, 1000);
      })
      .catch((err) => console.error("Failed to copy info:", err));
  };

  const orderName = attributes?.vendorId == 2 ? attributes?.externalOrderId : attributes?.orderName
  const displayedOrderName = orderName?.length > 30 ? `${orderName.slice(0, 30)}…` : orderName
  const orderNameHeading = (
    <h2
      className="heading-title"
      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {displayedOrderName}
    </h2>
  )

  return (
    <div className="order-2nd-div">
      <div className="w-layout-grid main-info-block-container">
        {/* ---- Info Block ---- */}
        <div className="info-block-container">
          <div>
            {orderName?.length > 30 ? (
              <Tooltip
                title={orderName}
                popperOptions={{ modifiers: { flip: { enabled: false } } }}
              >
                {orderNameHeading}
              </Tooltip>
            ) : orderNameHeading}
            <div className="w-layout-grid main-grid-info-order">
              {/* Photographer */}
              <div className="w-layout-grid grid-info-order">
                <div className="text-details-container text-bold">Photographer:</div>
                <div className="w-layout-grid grid-23 main _2">
                  <div className="icon small x2 w-embed"><PersonIcon /></div>
                  <div>{attributes?.photographerName ?? "-"}</div>
                </div>
                <div className="w-layout-grid grid-23 main _2">
                  <div className="icon small x2 w-embed"><BussinesIcon /></div>
                  <div>{attributes?.photographerBusinessName ?? "-"}</div>
                </div>
                <div className="w-layout-grid grid-23 main _2">
                  <div className="icon small x2 w-embed"><EnvelopeIcon /></div>
                  <div>{attributes?.photographerEmail ?? "-"}</div>
                </div>
              </div>

              {/* Shipping */}
              <div className="w-layout-grid grid-info-order">
                <div className="text-details-container text-bold">Shipping to:</div>
                <div className="w-layout-grid grid-23 main _2">
                  <div className="icon small x2 w-embed"><PersonIcon /></div>
                  <div>{address.firstName || address.lastName ? `${address.firstName} ${address.lastName}`.trim() : address.recipient ?? "-"}</div>
                </div>
                <div className="w-layout-grid grid-23 main _2">
                  <div className="icon small x2 w-embed"><LocationIcon /></div>
                  {!isEmpty(address) ? (
                    <div>
                      {address.street} <br />
                      {`${address.city} - ${address.stateProvince} ${address.zip}`}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
                {/* Copy/Edit Buttons */}
                {isAdmin && (
                  <div className="w-layout-grid grid-button-notes">
                    <button
                      className="_2nd-button main smaller transparent w-inline-block"
                      onClick={copyOrderDetails}
                    >
                      <div className="icon smaller right-margin w-embed">
                        {isCopy ? <AcceptIcon /> : <CopyIcon />}
                      </div>
                      <div >Copy</div>
                    </button>

                    {attributes?.shippingAddress?.guid && <button
                      className="_2nd-button main smaller transparent w-inline-block"
                      onClick={(e) => {
                        e.preventDefault();
                        openEditModal("address");
                      }}
                    >
                      <div className="icon smaller right-margin w-embed"><EditIcon /></div>
                      <div>Edit</div>
                    </button>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ---- Notes Tabs ---- */}
        <ShowSeparateNotes />
      </div>

      {/* Notes Modal */}
      <EditModal
        loading={loading}
        isOpen={isNotesModalOpen}
        onClose={handleNotesModalClose}
        title={`Add / Edit ${capitalizeFirstLetter(activeTab)} Notes`}
        icon={EditIcon}
        fields={[
          {
            placeholder: `${capitalizeFirstLetter(activeTab)} Notes`,
            value: editNotes[activeTab] ?? "",
            onChange: handleNoteChange,
            required: true,
          },
        ]}
        onSave={saveNotes}
      />

      {/* Address Modal */}
      <EditModal
        loading={loading}
        isOpen={isAddressModalOpen}
        onClose={handleAddressModalClose}
        title="Edit Shipping Address"
        icon={EditIcon}
        fields={[
          {
            placeholder: "First Name",
            value: editAddress.firstName ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, firstName: val }),
            required: true,
          },
          {
            placeholder: "Last Name",
            value: editAddress.lastName ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, lastName: val }),
            required: true,
          },
          {
            placeholder: "Street Address",
            value: editAddress.street ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, street: val }),
            required: true,
          },
          {
            placeholder: "Street Address 2",
            value: editAddress.street2 ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, street2: val }),
          },
          {
            placeholder: "City",
            value: editAddress.city ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, city: val }),
            required: true,
          },
          {
            type: "select",
            placeholder: "Select State",
            value: editAddress?.stateProvince ?? "",
            options: usStates.map((s) => ({ label: s.name, value: s.abbreviation })),
            onChange: (val) => setEditAddress({ ...editAddress, stateProvince: val }),
            required: true,
          },
          {
            placeholder: "Zip Code",
            value: editAddress.zip ?? "",
            onChange: (val) => setEditAddress({ ...editAddress, zip: val }),
            required: true,
          },
        ]}
        onSave={saveAddress}
      />
    </div>
  );
};

export default Details;
